import _ from 'lodash';
import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, makeStyles } from 'design-system';
import Kbd from 'design-system/Kbd';
import { Modal, ModalBody, ModalHeader } from 'design-system/Modal';
import BenchmarkThresholdAuditSidebar from 'react-scale/components/audits/SpeedAudit/BenchmarkThresholdAuditSidebar';
import FederalBanner from 'react-scale/components/ui/FederalBanner';
import useShortcut from 'react-scale/hooks/useShortcut';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { IS_FEDERAL } from '@scale/frontend-lib/constants';
import { useAsyncFn, useMount } from 'react-use-v13';
import {
  AuditResult,
  AuditResultV2,
  FlagAuditResult,
  fullPageModalLayer,
  isLidar,
  SpeedAuditVersion,
} from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import { AuditType } from '@scale/shared/types/corp/attemptAudit';
import { IWorkerIdToEmailMap } from '@scale/shared/types/corp/quality';
import { IInstructionItemJson } from '@scale/shared/types/instructionitem';
import IIThresholdAuditDescription from './IIThresholdAuditDescription';
import SpeedAuditResults from './SpeedAuditResults';
import {
  AuditBatch,
  BenchmarkThresholdAuditBatch,
  resultColors,
  resultShortcuts,
  SpeedAuditLookup,
  SpeedAuditResult,
  SubtaskAssignment,
} from './types';
import { speedAuditEventPrefix, updateAttemptAudit } from './utils';
import Spinner from 'design-system/Spinner';
import { Actions } from 'react-use-v13/lib/useMap';
import SAlert from 'react-s-alert';
import logger from '@scale/frontend-lib/logging';
import { PartialRecord } from '@scale/shared/types/utils';
import {
  ElementClickedType,
  GeneralElementClicked,
} from '@scale/shared/types/events/GeneralElementClicked';
import { logCorpEvent } from '@scale/frontend-lib/analytics/log-corp-event';
import { Event } from '@scale/shared/types/events';
import { SpeedAuditOpened } from '@scale/shared/types/events/SpeedAudit';
import SpeedAuditHeaderV2 from 'react-scale/components/audits/SpeedAudit/SpeedAuditHeaderV2';
import { SpeedAuditMultiTaskResponse } from 'react-scale/components/audits/SpeedAudit/SpeedAuditMultiTaskResponse';
import SpeedAuditForm from 'react-scale/components/audits/SpeedAudit/SpeedAuditForm';
import FlagAuditForm from 'react-scale/components/audits/SpeedAudit/FlagAuditForm';
import SpeedAuditHeaderV1 from 'react-scale/components/audits/SpeedAudit/SpeedAuditHeaderV1';
import SpeedAuditResultsV2 from 'react-scale/components/audits/SpeedAudit/SpeedAuditResultsV2';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import { useQAOperationContext } from 'react-scale/components/remoAdmin/qaOperation/qaOperationContext';
import { QAOperationHeader } from 'react-scale/components/remoAdmin/qaOperation/QAOperationSubmitModal';
import { SpeedAuditV2ObjectNavigation } from './v2/ObjectNavigationButtons';
import { IUserJson } from '@scale/shared/types/user';
import ClassicLidarSpeedAuditLinks from './ClassicLidarSpeedAuditLinks';
import SfuSpeedAuditLinks from './SfuSpeedAuditLinks';

interface SpeedAuditDialogProps {
  onRequestClose: () => void;
  onUpdateAuditBatch: (auditBatch: any, attemptIds: string[]) => Promise<void>;
  projectId: string;
  projectInstrItems: IInstructionItemJson[];
  objectIds: string[];
  auditBatch: AuditBatch;
  flagAudit?: boolean;
  subtaskAssignments: PartialRecord<string, SubtaskAssignment>;
  speedAuditResults: PartialRecord<string, SpeedAuditResult>;
  speedAuditResultsActions: Actions<PartialRecord<string, SpeedAuditResult>>;
  currentAttemptAudit: SpeedAuditLookup | undefined;
  index: number;
  setIndex: (index: number) => void;
  nextIndex: () => void;
  prevIndex: () => void;
  nextUnauditedIndex: () => void;
  prevUnauditedIndex: () => void;
  speedAuditVersion?: SpeedAuditVersion;
  user: IUserJson;
}

// eslint-disable-next-line complexity
function SpeedAuditDialog({
  onRequestClose,
  auditBatch,
  flagAudit,
  onUpdateAuditBatch,
  projectId,
  projectInstrItems,
  objectIds,
  subtaskAssignments,
  speedAuditResults,
  speedAuditResultsActions,
  currentAttemptAudit,
  index,
  setIndex,
  nextIndex,
  prevIndex,
  nextUnauditedIndex,
  prevUnauditedIndex,
  speedAuditVersion,
  user,
}: SpeedAuditDialogProps) {
  const { setSelectedProject, setAuditBatchId } = useRemoAdminCtx();

  useEffect(() => {
    logCorpEvent(Event.SpeedAuditOpened, {
      audit_name: auditBatch.name,
      audit_id: auditBatch._id,
      audit_project: projectId,
      speed_audit_version: speedAuditVersion,
    } as SpeedAuditOpened);
    setSelectedProject(projectId);
    setAuditBatchId(auditBatch._id);
  }, [auditBatch._id, auditBatch.name, projectId, setSelectedProject, speedAuditVersion]);

  const { isInReview, qaOperation } = useQAOperationContext();
  const [isOperationModalOpen, setIsOperationModalOpen] = useState(false);
  const handleRequestClose = useCallback(async () => {
    await apiFetch(null, `corp-api/attemptAudit/auditedBy`, {
      method: 'POST',
      body: {
        auditBatchId: auditBatch._id,
      },
    });
    if (qaOperation) {
      setIsOperationModalOpen(true);
      return;
    }
    onRequestClose();
  }, [onRequestClose, qaOperation, auditBatch._id]);

  const handleRequestCloseV2 = useCallback(
    async (sendFeedback: boolean) => {
      await apiFetch(null, `corp-api/attemptAudit/auditedBy`, {
        method: 'POST',
        body: {
          auditBatchId: auditBatch._id,
        },
      });

      if (sendFeedback) {
        const courseIds = await apiFetch(null, `corp-api/attemptAudit/sendFeedback`, {
          method: 'POST',
          body: {
            auditBatchId: auditBatch._id,
          },
        });

        const message = courseIds
          .map(
            (course: Record<string, string>) =>
              `Created course ${course.courseId} for ${course.taskerEmail}`,
          )
          .join('\n');
        if (message) {
          alert(message);
        }
      }
      if (qaOperation) {
        setIsOperationModalOpen(true);
        return;
      }
      onRequestClose();
    },
    [onRequestClose, qaOperation, auditBatch._id],
  );

  const closeOperationModal = useCallback(() => setIsOperationModalOpen(false), []);

  const styles = useSpeedAuditDialogStyles();
  const speedAuditValues = useMemo(
    () =>
      objectIds.map(objectId => speedAuditResults[objectId]).filter(x => !!x) as SpeedAuditResult[],
    [speedAuditResults, objectIds],
  );
  const subtaskAssignmentValues = useMemo(
    () => Object.values(subtaskAssignments) as SubtaskAssignment[],
    [subtaskAssignments],
  );
  const currentId = useMemo(() => currentAttemptAudit?.auditLookup.id || '', [currentAttemptAudit]);
  const subtaskAssignment = useMemo(
    () => subtaskAssignments[currentId],
    [currentId, subtaskAssignments],
  );

  const [selectedAttempt, setSelectedAttempt] = useState<string | undefined>();
  useEffect(() => {
    if (!subtaskAssignment) return;
    // When a new subtask is opened, we set the current selection to the first response.
    // This value is also set through the `onNavChange` callback of MultiTaskResponse.
    // Sometimes there is no ID more specific than the attempt ID,
    // (if we are reviewing a training attempt, for example), in which case we just use that ID.
    setSelectedAttempt(subtaskAssignment.responses?.[0]?.id ?? currentId);
  }, [setSelectedAttempt, currentId, subtaskAssignment]);

  const numObjects = objectIds.length;
  const speedAuditResult = useMemo(
    () => speedAuditResults[currentId],
    [currentId, speedAuditResults],
  );
  const numSubtaskAssignments = useMemo(() => _.size(subtaskAssignments), [subtaskAssignments]);
  const progress = useMemo(() => index / numObjects, [index, numObjects]);
  const progressPct = useMemo(() => Math.round(progress * 100), [progress]);
  const isCompleted = useMemo(
    () => index + 1 === numObjects && !!speedAuditResult?.result,
    [index, numObjects, speedAuditResult],
  );

  const [triggerShortcutV2, setTriggerShortcutV2] = useState<boolean>(false);
  const [triggerShortcutValueV2, setTriggerShortcutValueV2] = useState<AuditResultV2>(
    AuditResultV2.Ok,
  );
  const [isWritingFeedback, setIsWritingFeedback] = useState<boolean>(false);

  const isBenchmarkIIAudit = auditBatch.auditType === AuditType.BENCHMARK_II_THRESHOLDS;
  const isMissingRequiredGrade = isBenchmarkIIAudit && !_.get(speedAuditResult, 'result');

  const [selectedAnnotation, setSelectedAnnotation] = useState<string>();
  const [showHelpModal, setShowHelpModal] = React.useState<boolean>(false);
  const focusRef = React.useRef<HTMLDivElement>(null);

  const [workerIdToEmailMap, fetchWorkerIdToEmailMap] = useAsyncFn(async () => {
    return apiFetch<IWorkerIdToEmailMap>(
      null,
      `corp-api/quality/audits/batch/${auditBatch._id}/worker_info`,
      {
        method: 'GET',
      },
    );
  }, [auditBatch._id]);

  const [showResultsV2, setShowResultsV2] = useState(false);

  useMount(() => {
    if (focusRef.current) {
      focusRef.current.focus();
    }
    if (isBenchmarkIIAudit) {
      fetchWorkerIdToEmailMap().catch(e => {
        SAlert.error(`Failed to load Worker ID to Email Map`);
        logger.error(e);
      });
    }
  });

  const setCurrentAudit = useCallback(
    (result: SpeedAuditResult) => {
      const indexOfAudit = _.findIndex(speedAuditValues, r => r?.objectId === result.objectId);
      if (indexOfAudit > -1) {
        setIndex(indexOfAudit);
      }
    },
    [setIndex, speedAuditValues],
  );

  /** Navigation between objects in the Speed Audit Batch */

  const onPrev = useCallback(() => {
    prevIndex();
    setSelectedAnnotation(undefined); // Make sure you can't tag prev frame annotations on next tasks
    // @ts-ignore
    logCorpEvent(Event.GeneralElementClicked, {
      name: `${speedAuditEventPrefix(speedAuditVersion)} > Navigate to prev subtask`,
      type: ElementClickedType.Button,
    } as GeneralElementClicked);
  }, [prevIndex, speedAuditVersion]);

  const onNext = useCallback(() => {
    logCorpEvent(Event.GeneralElementClicked, {
      name: `${speedAuditEventPrefix(speedAuditVersion)} > Navigate to next subtask`,
      type: ElementClickedType.Button,
    } as GeneralElementClicked);

    nextIndex();
    setSelectedAnnotation(undefined); // Make sure you can't tag prev frame annotations on next tasks
  }, [nextIndex, speedAuditVersion]);

  const onPrevUnaudited = useCallback(() => {
    logCorpEvent(Event.GeneralElementClicked, {
      name: `${speedAuditEventPrefix(speedAuditVersion)} > Navigate to prev unaudited subtask`,
      type: ElementClickedType.Button,
    } as GeneralElementClicked);

    prevUnauditedIndex();
    setSelectedAnnotation(undefined); // Make sure you can't tag prev frame annotations on next tasks
  }, [prevUnauditedIndex, speedAuditVersion]);

  const onNextUnaudited = useCallback(() => {
    logCorpEvent(Event.GeneralElementClicked, {
      name: `${speedAuditEventPrefix(speedAuditVersion)} > Navigate to next unaudited subtask`,
      type: ElementClickedType.Button,
    } as GeneralElementClicked);

    nextUnauditedIndex();
    setSelectedAnnotation(undefined); // Make sure you can't tag prev frame annotations on next tasks
  }, [nextUnauditedIndex, speedAuditVersion]);

  const onRestart = useCallback(() => {
    setIndex(0);
  }, [setIndex]);

  const updateResult = useCallback(
    async (
      speedAuditResult: SpeedAuditResult,
      newSpeedAuditResultData: Partial<SpeedAuditResult>,
    ) => {
      speedAuditResultsActions.set(speedAuditResult.objectId, {
        ...speedAuditResult,
        ...newSpeedAuditResultData,
      });
      try {
        await updateAttemptAudit({
          attemptAuditId: speedAuditResult.attemptAuditId,
          ...newSpeedAuditResultData,
        });
        SAlert.success(`Updated result for Object ${speedAuditResult.objectId}`);
      } catch (e) {
        SAlert.error(`Failed to update result for Object ${speedAuditResult.objectId}`);
        logger.error(e);
      }
    },
    [speedAuditResultsActions],
  );

  const updateResultFlag = useCallback(
    async (flagResult: FlagAuditResult, flagValue: string) => {
      if (!speedAuditResult) return;

      speedAuditResultsActions.set(speedAuditResult.objectId, { ...speedAuditResult, flagResult });
      try {
        await updateAttemptAudit({
          attemptAuditId: speedAuditResult.attemptAuditId,
          flagResult: flagValue,
        });
        SAlert.success(`Updated flag to ${flagValue} for Object ${speedAuditResult.objectId}`);
      } catch (e) {
        SAlert.error(`Failed to update flag for Object ${speedAuditResult.objectId}`);
        logger.error(e);
      }
    },
    [speedAuditResult, speedAuditResultsActions],
  );

  const updateResultStatus = useCallback(
    (result?: AuditResult) => {
      if (!speedAuditResult) return;
      return updateResult(speedAuditResult, { result });
    },
    [speedAuditResult, updateResult],
  );

  function triggerGradeSpeedAuditV2(newGrade: AuditResultV2) {
    setTriggerShortcutValueV2(newGrade);
    setTriggerShortcutV2(true);
    setTriggerShortcutV2(false);
  }

  useShortcut({
    trigger: 'a',
    action: () => {
      switch (speedAuditVersion) {
        case SpeedAuditVersion.v1:
          updateResultStatus(AuditResult.Perfect);
          break;
        case SpeedAuditVersion.v2:
          triggerGradeSpeedAuditV2(AuditResultV2.Perfect);
      }
    },
    description: 'Set Audit Result to Perfect',
    disabled: isBenchmarkIIAudit,
  });
  useShortcut({
    trigger: 'w',
    action: () => {
      switch (speedAuditVersion) {
        case SpeedAuditVersion.v1:
          updateResultStatus(AuditResult.Good);
          break;
        case SpeedAuditVersion.v2:
          triggerGradeSpeedAuditV2(AuditResultV2.Good);
      }
    },
    description: 'Set Audit Result to Good',
  });
  useShortcut({
    trigger: 's',
    action: () => updateResultStatus(),
    description: 'Reset Audit Result',
  });
  useShortcut({
    trigger: 'd',
    action: () => {
      switch (speedAuditVersion) {
        case SpeedAuditVersion.v1:
          updateResultStatus(AuditResult.Ok);
          break;
        case SpeedAuditVersion.v2:
          triggerGradeSpeedAuditV2(AuditResultV2.Ok);
      }
    },
    description: 'Set Audit Result to Ok',
  });
  useShortcut({
    trigger: 'r',
    action: () => {
      switch (speedAuditVersion) {
        case SpeedAuditVersion.v1:
          updateResultStatus(AuditResult.Bad);
          break;
        case SpeedAuditVersion.v2:
          triggerGradeSpeedAuditV2(AuditResultV2.Bad);
      }
    },
    description: 'Set Audit Result to Bad',
    disabled: isBenchmarkIIAudit,
  });
  useShortcut({
    trigger: 'f',
    action: () => {
      switch (speedAuditVersion) {
        case SpeedAuditVersion.v1:
          updateResultStatus(AuditResult.Terrible);
          break;
        case SpeedAuditVersion.v2:
          triggerGradeSpeedAuditV2(AuditResultV2.Terrible);
      }
    },
    description: 'Set Audit Result to Terrible',
  });
  useShortcut({
    trigger: '/',
    action: () => !isWritingFeedback && setShowHelpModal(true),
    description: 'Show Help Modal',
  });

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (
        subtaskAssignment &&
        subtaskAssignment.subtask.type &&
        isLidar(subtaskAssignment.subtask.type)
      ) {
        // lidar viewer is unusable without its keys
        return;
      }
      switch (event.keyCode) {
        case 37: // Left arrow
          event.shiftKey ? onPrevUnaudited() : onPrev();
          break;
        case 39: // Right arrow
          !isMissingRequiredGrade && (event.shiftKey ? onNextUnaudited() : onNext());
          break;
        case 191: // Slash (/)
          !isWritingFeedback && setShowHelpModal(true);
          break;
      }
    },
    [
      subtaskAssignment,
      onPrevUnaudited,
      onPrev,
      isMissingRequiredGrade,
      onNextUnaudited,
      onNext,
      isWritingFeedback,
    ],
  );

  const handleShowHelpModal = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      !isWritingFeedback && setShowHelpModal(true);
    },
    [isWritingFeedback],
  );

  const hideHelpModal = useCallback(() => {
    setShowHelpModal(false);
  }, []);

  const headerId = useMemo(
    () =>
      (flagAudit ? subtaskAssignment?.subtask?.id : subtaskAssignment?.auditLookup.id) ||
      currentAttemptAudit?.auditLookup.id,
    [flagAudit, subtaskAssignment, currentAttemptAudit],
  );

  let metadataDescription;
  if (isBenchmarkIIAudit) {
    const metadata = (auditBatch as BenchmarkThresholdAuditBatch).attemptAudits[index]?.metadata;
    metadataDescription = metadata && (
      <IIThresholdAuditDescription metadata={metadata} projectInstrItems={projectInstrItems} />
    );
  }

  const isReviewSBQ = useMemo(
    () =>
      subtaskAssignment?.responses.find(response => response.id === headerId)?.isSendBackToQueue,
    [headerId, subtaskAssignment],
  );

  const onNavChangeCallback = ({ id }: { id?: string }) => {
    setSelectedAttempt(id ?? currentId);
    if (id) {
      logCorpEvent(Event.SpeedAuditAttemptClicked, {
        audit_id: auditBatch._id,
        audit_project: projectId,
        attempt_id: id,
        speed_audit_version: speedAuditVersion,
      });
    }
  };

  const header =
    speedAuditVersion === SpeedAuditVersion.v1 ? (
      subtaskAssignment && currentAttemptAudit?.auditLookup ? (
        <SpeedAuditHeaderV1
          styles={styles}
          name={auditBatch.name}
          id={auditBatch._id}
          openKeyboardShortcuts={handleShowHelpModal}
          headerId={headerId}
          flagAudit={flagAudit}
          subtaskAssignment={subtaskAssignment}
          auditLookup={currentAttemptAudit?.auditLookup}
          index={index}
          onPreviousUnauditedObject={onPrevUnaudited}
          onPreviousObject={onPrev}
          numObjects={numObjects}
          numSubtaskAssignments={numSubtaskAssignments}
          completed={isCompleted}
          missingRequiredGrade={isMissingRequiredGrade}
          onNextObject={onNext}
          onNextUnauditedObject={onNextUnaudited}
          onRestart={onRestart}
          projectId={projectId}
          onRequestClose={handleRequestClose}
          metadataDescription={metadataDescription}
        />
      ) : (
        <span />
      )
    ) : subtaskAssignment && currentAttemptAudit?.auditLookup ? (
      <SpeedAuditHeaderV2
        styles={styles}
        name={auditBatch.name}
        id={auditBatch._id}
        openKeyboardShortcuts={handleShowHelpModal}
        headerId={headerId}
        flagAudit={flagAudit}
        subtaskAssignment={subtaskAssignment}
        auditLookup={currentAttemptAudit?.auditLookup}
        index={index}
        numObjects={numObjects}
        numSubtaskAssignments={numSubtaskAssignments}
        completed={isCompleted}
        missingRequiredGrade={isMissingRequiredGrade}
        onRestart={onRestart}
        projectId={projectId}
        metadataDescription={metadataDescription}
        completeAudit={handleRequestCloseV2}
        showResults={() => setShowResultsV2(true)}
      />
    ) : (
      <span />
    );

  return (
    <div className={styles.fullPageModal} onKeyDown={onKeyDown} tabIndex={0} ref={focusRef}>
      {IS_FEDERAL && <FederalBanner />}
      <div className={styles.progress}>
        <div className={styles.progressBar} style={{ width: `${progressPct}%` }} />
      </div>
      {qaOperation && (
        <QAOperationHeader
          isOpen={isOperationModalOpen}
          overrideSubmitButton={!isInReview()}
          onClose={closeOperationModal}
        />
      )}
      {header}
      {(!subtaskAssignment || !speedAuditResult) && <Spinner />}
      {subtaskAssignment && speedAuditResult && (
        <div className={styles.auditContainer}>
          {isBenchmarkIIAudit && (
            <BenchmarkThresholdAuditSidebar
              results={speedAuditValues}
              auditBatchMetadata={auditBatch.metadata}
              projectId={projectId}
              projectInstrItems={projectInstrItems}
              batchIdToUpdate={auditBatch._id}
              onUpdateAuditBatch={onUpdateAuditBatch}
              currentResponse={subtaskAssignment}
              workerIdToEmailMap={workerIdToEmailMap.value || {}}
            />
          )}
          <div className={styles.auditContent}>
            {speedAuditVersion === SpeedAuditVersion.v1 &&
              (flagAudit ? (
                <FlagAuditForm result={speedAuditResult} onChangeFlagResult={updateResultFlag} />
              ) : (
                <SpeedAuditForm
                  key={speedAuditResult.attemptAuditId}
                  result={speedAuditResult}
                  updateResult={updateResult}
                  auditBatchId={auditBatch._id}
                  projectId={projectId}
                  projectInstrItems={projectInstrItems}
                  selectedAnnotation={selectedAnnotation}
                  auditType={auditBatch.auditType}
                  isSBQ={isReviewSBQ}
                  speedAuditVersion={speedAuditVersion}
                />
              ))}
            <Suspense
              fallback={
                <div
                  style={
                    speedAuditVersion === SpeedAuditVersion.v1
                      ? { display: 'flex', flexDirection: 'column', justifyContent: 'center' }
                      : {
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          position: 'fixed',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                        }
                  }
                >
                  <Spinner size="lg" />
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                    Setting up workspace...
                  </div>
                </div>
              }
            >
              {subtaskAssignment.subtask.type &&
                (isLidar(subtaskAssignment.subtask.type) ||
                  subtaskAssignment.subtask.type === TaskType.SensorFusion) &&
                !subtaskAssignment.subtask.isSfuTask && (
                  <ClassicLidarSpeedAuditLinks subtaskAssignment={subtaskAssignment} />
                )}
              {subtaskAssignment.subtask.isSfuTask && (
                <SfuSpeedAuditLinks subtaskAssignment={subtaskAssignment} />
              )}
              {!subtaskAssignment.subtask.isSfuTask &&
                !(
                  subtaskAssignment.subtask.type &&
                  (isLidar(subtaskAssignment.subtask.type) ||
                    subtaskAssignment.subtask.type === TaskType.SensorFusion) &&
                  !subtaskAssignment.subtask.isSfuTask
                ) && (
                  <SpeedAuditMultiTaskResponse
                    subtaskAssignment={subtaskAssignment}
                    setSelectedAnnotation={setSelectedAnnotation}
                    onNavChange={onNavChangeCallback}
                    showTaskerPane={speedAuditVersion === SpeedAuditVersion.v2}
                  />
                )}
            </Suspense>
          </div>
        </div>
      )}
      {speedAuditVersion === SpeedAuditVersion.v2 && (
        <SpeedAuditV2ObjectNavigation
          {...{
            subtaskAssignment,
            speedAuditResult,
            currentAttemptAudit,
            selectedAttempt,
            triggerShortcutV2,
            triggerShortcutValueV2,
            projectId,
            isCompleted,
            index,
            numObjects,
            numSubtaskAssignments,
            isMissingRequiredGrade,
            speedAuditResultsActions,
            onPrevUnaudited,
            onNextUnaudited,
            onPrev,
            onNext,
            user,
            setIsWritingFeedback,
          }}
        />
      )}
      {speedAuditVersion === SpeedAuditVersion.v1 && (
        <>
          <div className={styles.speedAuditResultsHeader}>
            <h3>
              Speed Audit Results for Batch <code>{auditBatch.name}</code>
            </h3>
            {numSubtaskAssignments !== numObjects && (
              <p>
                Warning: Not all attempts have been loaded and audited - loaded{' '}
                {numSubtaskAssignments} / {numObjects} attempts
              </p>
            )}
            {!isCompleted && (
              <p>
                Warning: Not all attempts have been audited - currently auditing attempt {index + 1}{' '}
                / {numObjects}
              </p>
            )}
          </div>
          <SpeedAuditResults
            results={speedAuditValues}
            isBenchmarkIIAudit={isBenchmarkIIAudit}
            projectInstrItems={projectInstrItems}
            auditBatch={auditBatch as BenchmarkThresholdAuditBatch}
            matchingResponses={subtaskAssignmentValues}
            workerIdToEmailMap={workerIdToEmailMap.value || {}}
            setCurrentAudit={setCurrentAudit}
          />
        </>
      )}
      {speedAuditVersion === SpeedAuditVersion.v2 && showResultsV2 && (
        <Dialog open={showResultsV2} onClose={() => setShowResultsV2(false)}>
          <SpeedAuditResultsV2
            auditBatchId={auditBatch._id}
            subtaskAssignments={subtaskAssignments}
          />
        </Dialog>
      )}
      <Modal isOpen={showHelpModal} onRequestClose={hideHelpModal}>
        <ModalHeader onRequestClose={hideHelpModal}>Keyboard Shortcuts</ModalHeader>
        <ModalBody>
          <div className={styles.shortcut}>
            <Kbd>&#8592;</Kbd>
            <span className="shortcutText">Previous Object (Left Arrow Key)</span>
          </div>
          <div className={styles.shortcut}>
            <Kbd>&#8594;</Kbd>
            <span className="shortcutText">Next Object (Right Arrow Key)</span>
          </div>
          <div className={styles.shortcut}>
            <Kbd>&#8592;</Kbd> + <Kbd>Shift</Kbd>
            <span className="shortcutText">
              Skip to Previous Unaudited Object (Shift + Left Arrow Key)
            </span>
          </div>
          <div className={styles.shortcut}>
            <Kbd>&#8594;</Kbd> + <Kbd>Shift</Kbd>
            <span className="shortcutText">
              Skip to Next Unaudited Object (Shift + Right Arrow Key)
            </span>
          </div>
          {speedAuditVersion === SpeedAuditVersion.v1 &&
            _.values(AuditResult).map(score => (
              <div key={score} className={styles.shortcut}>
                <Kbd>{resultShortcuts[score]}</Kbd>
                <span className={styles.shortcutText}>
                  Assign grade "{score}" for{' '}
                  <span style={{ color: resultColors[score], fontWeight: 500 }}>{score}</span>.
                </span>
              </div>
            ))}
          {speedAuditVersion === SpeedAuditVersion.v2 &&
            _.values(AuditResultV2).map(score => (
              <div key={score} className={styles.shortcut}>
                <Kbd>{resultShortcuts[score]}</Kbd>
                <span className={styles.shortcutText}>
                  Assign grade "{score}" for{' '}
                  <span style={{ color: resultColors[score], fontWeight: 500 }}>{score}</span>.
                </span>
              </div>
            ))}
        </ModalBody>
      </Modal>
      {IS_FEDERAL && <FederalBanner />}
    </div>
  );
}

const useSpeedAuditDialogStyles = makeStyles(theme => ({
  fullPageModal: {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100vw',
    height: '100vh',
    background: 'white',
    zIndex: fullPageModalLayer,
    overflowY: 'auto',
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderBottom: '1px solid var(--color-RemoGray01)',
    width: '100%',
  },
  header: {
    flex: 1,
    paddingTop: '16px',
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingBottom: '16px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  block: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  blockRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '0 24px 16px 24px',
  },
  blockComplete: {
    backgroundColor: theme.colors.green,
    borderRadius: 4,
  },
  blockCompleteContent: {
    marginBottom: 4,
  },
  blockRightButtons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    '& > *:not(:first-child)': {
      marginLeft: 4,
    },
  },
  title: {
    fontSize: '18px',
    lineHeight: '24px',
    margin: '0',
    padding: '0',
  },
  nav: {
    marginLeft: '24px',
    marginRight: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    flex: '1',
  },
  progress: {
    height: '4px',
    background: '#f7f7f7',
    width: '100%',
  },
  progressBar: {
    height: '4px',
    background: theme.colors.blue,
    opacity: '0.5',
  },
  restart: {
    padding: '24px',
  },
  helpLink: {
    marginLeft: '12px',
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  helpLinkText: {
    paddingLeft: '4px',
    paddingRight: '2px',
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: 500,
  },
  shortcut: {
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  shortcutText: {
    marginLeft: '12px',
    fontSize: '16px',
    lineHeight: '24px',
  },
  auditContainer: {
    display: 'flex',
  },
  auditContent: {
    overflow: 'auto',
    flexGrow: 1,
  },
  speedAuditResultsHeader: {
    marginLeft: 24,
  },
  lidarLiteLinkContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    marginBottom: 8,
  },
}));

export default React.memo(SpeedAuditDialog);
